import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Box } from '@chakra-ui/react';
import { Card } from '@/components/v4';
import AGGridBase from 'components/tables/AGGrid/AGGridBase';
import AddUserLicenceTreeToolbar from './AddUserLicenceTreeToolbar';
import {
    AUTO_GROUP_COL_DEF,
    canAssignRow,
    COLUMN_DEFS,
    DEFAULT_COL_DEF,
    getAvailableQuantity,
    HEADER_CLASS,
} from './AddUserLicenceTree.constants';
import './AddUserLicenceTree.css';

const AddUserLicenceTree = ({
    catalogue = [],
    value = [],
    onChange,
    darkMode,
    isDisabled = false,
}) => {
    const [search, setSearch] = useState('');
    const gridRef = useRef();
    const selectedRef = useRef(new Set(value));

    // Keep selectedRef current and force grid cell refresh on selection change
    useEffect(() => {
        selectedRef.current = new Set(value);
        gridRef.current?.api?.refreshCells({ force: true });
    }, [value]);

    const handleToggle = useCallback(
        (id) => {
            if (isDisabled) return;
            const item = catalogue.find((c) => c.id === id);
            if (!item || item.required) return;
            const next = new Set(selectedRef.current);
            if (next.has(id)) {
                next.delete(id);
                // Cascade: remove MUST children when parent is removed
                catalogue.forEach((c) => {
                    if (c.parentId === id && c.required) next.delete(c.id);
                });
            } else {
                if (!canAssignRow(item, catalogue, next)) return;
                next.add(id);
                // Cascade: add MUST children when parent is added
                catalogue.forEach((c) => {
                    if (c.parentId === id && c.required) next.add(c.id);
                });
            }
            onChange([...next]);
        },
        [catalogue, onChange, isDisabled],
    );

    const handleSelectAll = useCallback(() => {
        if (isDisabled) return;
        const next = new Set(
            catalogue
                .filter(
                    (item) =>
                        !item.required &&
                        (selectedRef.current.has(item.id) ||
                            canAssignRow(item, catalogue, selectedRef.current)),
                )
                .map((item) => item.id),
        );
        // Auto-include MUST children of selected parents
        catalogue.forEach((item) => {
            if (item.required && next.has(item.parentId)) next.add(item.id);
        });
        onChange([...next]);
    }, [catalogue, onChange, isDisabled]);

    const handleDeselectAll = useCallback(() => {
        if (isDisabled) return;
        onChange([]);
    }, [onChange, isDisabled]);

    // Build flat row array with path[] so AG Grid can construct the tree
    const rowData = useMemo(() => {
        const byId = {};
        catalogue.forEach((item) => {
            byId[item.id] = item;
        });

        const getPath = (item) => {
            const path = [];
            let cur = item;
            while (cur) {
                path.unshift(cur.id);
                cur = cur.parentId ? byId[cur.parentId] : null;
            }
            return path;
        };

        return catalogue.map((item) => {
            const totalDefined = item.total != null;
            const available = getAvailableQuantity(item);
            return {
                ...item,
                path: getPath(item),
                available,
                isAtLimit: totalDefined && available !== null && available <= 0,
            };
        });
    }, [catalogue]);

    // Context is read inside cell renderers; selectedRef is stable so not in deps
    const context = useMemo(
        () => ({ selectedRef, handleToggle, darkMode, catalogue }),
        [handleToggle, darkMode, catalogue],
    );

    const selected = useMemo(() => new Set(value), [value]);
    const selectableIds = useMemo(
        () =>
            catalogue
                .filter(
                    (i) =>
                        !i.required &&
                        (selected.has(i.id) ||
                            canAssignRow(i, catalogue, selected)),
                )
                .map((i) => i.id),
        [catalogue, selected],
    );
    const selectedCount = selectableIds.filter((id) => selected.has(id)).length;
    const allSelected =
        selectableIds.length > 0 && selectedCount === selectableIds.length;
    const noneSelected = selectedCount === 0;

    const borderColor = darkMode ? 'dark.tcap-borderGray' : 'gray.200';
    const searchbarBg = darkMode ? 'dark.tcap-weak' : '#EEEEEE';

    return (
        <Box className={`${HEADER_CLASS}`}>
            <Card overflow="hidden" bg={darkMode ? 'dark.tcap' : 'white'}>
                <Box px={4} pt={4} pb={2} bg={searchbarBg}>
                    <AddUserLicenceTreeToolbar
                        onSelectAll={handleSelectAll}
                        onDeselectAll={handleDeselectAll}
                        allSelected={allSelected}
                        noneSelected={noneSelected}
                        search={search}
                        onSearchChange={setSearch}
                        darkMode={darkMode}
                        borderColor={borderColor}
                        isDisabled={isDisabled}
                    />
                </Box>
                <Box
                    opacity={isDisabled ? 0.6 : 1}
                    pointerEvents={isDisabled ? 'none' : 'auto'}>
                    <AGGridBase
                        gridRef={gridRef}
                        tableData={{ columns: COLUMN_DEFS, data: rowData }}
                        autoGroupColumnDef={AUTO_GROUP_COL_DEF}
                        defaultColDef={DEFAULT_COL_DEF}
                        pagination={false}
                        useFixedHeight={false}
                        customHeight="300px"
                        quickFilterText={search}
                        context={context}
                        treeData
                        groupDisplayType="singleColumn"
                        getDataPath={(data) => data.path}
                        getRowId={(params) => params.data.id}
                    />
                </Box>
            </Card>
        </Box>
    );
};

export default AddUserLicenceTree;
